/**
 * PublicRecordDetail — hero image + uniform field grid for record detail pages.
 *
 * Layout:
 *   • Hero: cover image (if image_field mapped) or themed gradient placeholder.
 *   • Description + tags: mapped description renders as a dedicated block
 *     above the grid, with tag badges (from tags_field) directly below it.
 *   • Field grid: every other visible field — including Field Mapping fields
 *     (title, subtitle, badge) — in a uniform label / value format. Column
 *     count controlled by detailColumns prop (1 / 2 / 3).
 *   • The image, description, and tags fields are excluded from the grid
 *     (already shown as hero / dedicated block above it).
 */

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '../../theme/ThemeContext';
import PublicFieldValue from './PublicFieldValue';
import PublicTagBadges from './PublicTagBadges';
import envConfig from '../../config/envConfig';
import { getCurrencyDisplay } from '../../utils/currencies';
import { toDisplayString, zoneOf } from '../../utils/utils';

function resolveFileUrl(value) {
  if (!value) return null;
  if (value.startsWith('http://') || value.startsWith('https://')) return value;
  return `${envConfig.fileServerUrl}/public/${value}`;
}

// Field types that are always short — never need a truncation tooltip
const NO_TRUNCATE_TYPES = new Set([
  'boolean', 'enum', 'date', 'datetime', 'image', 'file',
  'integer', 'float', 'decimal', 'numeric',
  'fk', 'one_to_one', 'one_to_many',
]);

function getGridCols(cols) {
  if (cols === 1) return '1fr';
  if (cols === 3) return 'repeat(3, minmax(0, 1fr))';
  return 'repeat(2, minmax(0, 1fr))'; // 2 columns default
}

export default function PublicRecordDetail({ record = {}, fields = [], mapping = {}, branding = {}, tableSlug = '', detailColumns = 2 }) {
  const [tooltip, setTooltip] = useState(null);
  const [lightbox, setLightbox] = useState(false);
  const { theme } = useTheme();

  // Close lightbox on ESC key
  useEffect(() => {
    if (!lightbox) return;
    const handler = (e) => { if (e.key === 'Escape') setLightbox(false); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightbox]);

  function showTooltip(e, fieldName, content) {
    const rect = e.currentTarget.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - 344));
    if (spaceBelow > 230) {
      setTooltip({ fieldName, content, top: rect.bottom + 8, left });
    } else {
      setTooltip({ fieldName, content, bottom: window.innerHeight - rect.top + 8, left });
    }
  }

  const badgeColors = mapping.badge_colors || {};
  const accentColor = theme.primary;

  const fieldMap = Object.fromEntries(fields.map(f => [f.name, f]));

  // Image field — only used for the hero cover, excluded from the grid
  const imageMappedMeta  = mapping.image_field ? fieldMap[mapping.image_field] : null;
  const isRealImageField = imageMappedMeta?.data_type === 'image';
  const imageRawVal      = (isRealImageField && mapping.image_field)
    ? record[mapping.image_field]
    : null;
  const imageUrl   = resolveFileUrl(imageRawVal);
  const hasImage   = !!imageUrl;

  const descriptionVal = mapping.description_field ? record[mapping.description_field] : null;
  const isDescBlank = descriptionVal === null || descriptionVal === undefined || descriptionVal === '';
  const tagsVal = mapping.tags_field ? record[mapping.tags_field] : null;
  const isTagsBlank = tagsVal === null || tagsVal === undefined || tagsVal === '';

  // All fields except the image field (shown as hero cover) and, when
  // present, description/tags (shown as a dedicated block above the grid).
  const gridFields = fields.filter(f =>
    !(isRealImageField && f.name === mapping.image_field) &&
    !(!isDescBlank && f.name === mapping.description_field) &&
    !(!isTagsBlank && f.name === mapping.tags_field)
  );

  const titleVal = mapping.title_field ? record[mapping.title_field] : null;

  const gridCols = getGridCols(Number(detailColumns) || 2);

  return (
    <>
    <div style={{
      borderRadius: 'var(--card-radius, 12px)',
      overflow: 'hidden',
      background: '#fff',
      boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
    }}>

      {/* ── Hero: cover image or themed gradient placeholder ─────────────── */}
      {/* 2:1 by width rather than a fixed 220px, so `cover` keeps most of a
          16:9 photo instead of a thin strip; capped so it never towers. */}
      <div style={{ position: 'relative', width: '100%', aspectRatio: '2 / 1', minHeight: 220, maxHeight: 420, flexShrink: 0 }}>
        {hasImage ? (
          <img
            src={imageUrl}
            alt={titleVal || 'cover'}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', cursor: 'zoom-in' }}
            onClick={() => setLightbox(true)}
            onError={e => { e.target.style.display = 'none'; }}
          />
        ) : (
          <div style={{
            width: '100%', height: '100%',
            background: `linear-gradient(135deg, ${accentColor}28 0%, ${accentColor}55 100%)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            position: 'relative', overflow: 'hidden',
          }}>
            <div style={{
              position: 'absolute', inset: 0,
              backgroundImage: `
                radial-gradient(ellipse at 15% 85%, ${accentColor}44 0%, transparent 55%),
                radial-gradient(ellipse at 85% 15%, ${accentColor}33 0%, transparent 55%)
              `,
            }} />
            <div style={{
              position: 'absolute', inset: 0,
              backgroundImage: `radial-gradient(circle, ${accentColor}22 1px, transparent 1px)`,
              backgroundSize: '22px 22px', opacity: 0.6,
            }} />
            <img
              src="/favicon.svg"
              alt="Arcee"
              style={{ position: 'relative', width: 'clamp(4rem, 14vw, 7rem)', height: 'clamp(4rem, 14vw, 7rem)', opacity: 0.25, userSelect: 'none' }}
            />
          </div>
        )}
        {hasImage && (
          <div style={{
            position: 'absolute', bottom: 0, left: 0, right: 0, height: 60,
            background: 'linear-gradient(to bottom, transparent, rgba(0,0,0,0.15))',
            pointerEvents: 'none',
          }} />
        )}
      </div>

      {/* ── Field grid — all remaining fields in uniform label / value format ── */}
      <div style={{ padding: '1.25rem 1.75rem 1.5rem' }}>
        {/* Description + tags — dedicated block, not part of the uniform grid */}
        {(!isDescBlank || !isTagsBlank) && (
          <div style={{ marginBottom: gridFields.length > 0 ? '1.25rem' : 0 }}>
            {!isDescBlank && (
              <p style={{
                margin: isTagsBlank ? 0 : '0 0 0.75rem',
                fontSize: '0.95rem',
                lineHeight: 1.7,
                color: '#334155',
                whiteSpace: 'pre-wrap',
              }}>
                {toDisplayString(descriptionVal)}
              </p>
            )}
            {!isTagsBlank && (
              <PublicTagBadges value={tagsVal} bgColor={mapping.tag_bg_color} accentColor={accentColor} />
            )}
          </div>
        )}

        {gridFields.length > 0 ? (
          <div style={{
            display: 'grid',
            gridTemplateColumns: gridCols,
            gap: '0.75rem 1.5rem',
          }}>
            {gridFields.map(field => {
              const val      = record[field.name];
              const isEmpty  = val === null || val === undefined || val === '';
              const badgeColor = (!isEmpty && field.data_type === 'enum')
                ? (badgeColors[val] || 'primary')
                : undefined;
              const isJson = field.data_type === 'json' || field.data_type === 'jsonb';
              const canTruncate = !isEmpty && !isJson && !NO_TRUNCATE_TYPES.has(field.data_type);
              const isCostField = mapping.cost_field && field.name === mapping.cost_field;
              const currencySymbol = isCostField ? getCurrencyDisplay(mapping.cost_currency) : undefined;

              return (
                <div
                  key={field.name}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = '#f8fafc';
                    if (canTruncate) showTooltip(e, field.name, toDisplayString(val));
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = 'transparent';
                    if (canTruncate) setTooltip(null);
                  }}
                  style={{
                    borderBottom: '1px solid #f1f5f9',
                    paddingBottom: '0.55rem',
                    paddingTop: '0.35rem',
                    paddingLeft: '0.4rem',
                    paddingRight: '0.4rem',
                    borderRadius: 8,
                    transition: 'background 150ms ease',
                  }}
                >
                  <div style={{
                    fontSize: '0.68rem', fontWeight: 700,
                    textTransform: 'uppercase', letterSpacing: '0.06em',
                    color: '#94a3b8', marginBottom: 4,
                  }}>
                    {field.display_name || field.name}
                  </div>
                  <div style={{
                    fontSize: '0.9rem', color: '#1e293b',
                    ...(isJson
                      ? { maxHeight: '12rem', overflow: 'auto' }
                      : (canTruncate ? { maxHeight: '4.35rem', overflow: 'hidden' } : {})),
                  }}>
                    {isEmpty
                      ? <span style={{ color: '#94a3b8' }}>—</span>
                      : <PublicFieldValue field={field} value={val} badgeColor={badgeColor} currencySymbol={currencySymbol} timeZone={zoneOf(record)} />
                    }
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          isDescBlank && isTagsBlank && (
            <p style={{ color: '#94a3b8', fontSize: '0.9rem', margin: 0 }}>No fields to display.</p>
          )
        )}
      </div>
    </div>

    {/* ── Lightbox portal ── */}
    {lightbox && hasImage && createPortal(
      <div
        onClick={() => setLightbox(false)}
        style={{
          position: 'fixed', inset: 0, zIndex: 99998,
          background: 'rgba(0,0,0,0.88)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'zoom-out',
        }}
      >
        <img
          src={imageUrl}
          alt={titleVal || 'cover'}
          onClick={e => e.stopPropagation()}
          style={{
            maxWidth: '92vw', maxHeight: '90vh',
            objectFit: 'contain',
            borderRadius: 8,
            boxShadow: '0 8px 48px rgba(0,0,0,0.6)',
            cursor: 'default',
          }}
        />
        <button
          onClick={() => setLightbox(false)}
          style={{
            position: 'absolute', top: 16, right: 20,
            background: 'rgba(255,255,255,0.12)', border: 'none',
            color: '#fff', fontSize: '1.4rem', lineHeight: 1,
            width: 38, height: 38, borderRadius: '50%',
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          aria-label="Close"
        >
          ×
        </button>
      </div>,
      document.body
    )}

    {/* ── Tooltip portal ── */}
    {tooltip && createPortal(
      <div style={{
        position: 'fixed',
        top: tooltip.top ?? 'auto', bottom: tooltip.bottom ?? 'auto',
        left: tooltip.left, width: 320, maxHeight: 220, overflowY: 'auto',
        background: '#1e293b', color: '#f1f5f9',
        fontSize: '0.82rem', lineHeight: 1.65,
        padding: '10px 14px', borderRadius: 10,
        boxShadow: '0 8px 28px rgba(0,0,0,0.28), 0 2px 8px rgba(0,0,0,0.16)',
        zIndex: 99999, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        pointerEvents: 'none', borderLeft: `3px solid ${accentColor}`,
      }}>
        {tooltip.content}
      </div>,
      document.body
    )}
    </>
  );
}
